import { type ChangeEvent, useEffect, useState } from 'react';
import classNames from 'classnames';

import { Card } from '@/components/atoms/Card';
import { Caption } from '@/components/atoms/Eyebrow';
import TextField from '@/components/atoms/TextField';

import { formatWinchMeters, WINCH_VALUE_STEP } from '../../helpers/formatWinchValue';

import styles from './PositionValue.module.scss';

interface PositionValueProps {
    label: string;
    value: number | null;
    unit: string;
    muted?: boolean;
    caption?: string;
    invalid?: boolean;
    editing?: boolean;
    inputId?: string;
    step?: number;
    onChange?: (value: number | null) => void;
    nested?: boolean;
}

function toInputText(value: number | null | undefined): string {
    if (value == null || Number.isNaN(value)) {
        return '';
    }
    return String(value);
}

function parseInputNumber(raw: string): number | null {
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
        return null;
    }
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}

export function PositionValue(props: PositionValueProps) {
    const {
        label,
        value,
        unit,
        muted = false,
        caption,
        invalid = false,
        editing = false,
        inputId,
        step = WINCH_VALUE_STEP,
        onChange,
        nested = false,
    } = props;
    const [text, setText] = useState(() => toInputText(value));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (editing && !focused) {
            setText(toInputText(value));
        }
    }, [editing, focused, value]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const next = event.target.value;
        setText(next);
        onChange?.(parseInputNumber(next));
    };

    return (
        <Card
            variant={nested ? 'surface' : 'subtle'}
            radius="sm"
            fullWidth={false}
            className={classNames(styles.valueCard, {
                [styles.muted]: muted && !editing && !invalid,
                [styles.invalid]: invalid,
            })}>
            {editing ? (
                <Caption as="label" htmlFor={inputId}>
                    {label}
                </Caption>
            ) : (
                <Caption>{label}</Caption>
            )}
            {editing ? (
                <div className={styles.inputRow}>
                    <TextField
                        id={inputId}
                        className={styles.input}
                        type="number"
                        step={step}
                        value={text}
                        aria-invalid={invalid}
                        aria-describedby={
                            invalid && caption && inputId ? `${inputId}-error` : undefined
                        }
                        onFocus={() => setFocused(true)}
                        onBlur={() => setFocused(false)}
                        onChange={handleChange}
                    />
                    <span className={styles.unit}>{unit}</span>
                </div>
            ) : (
                <span className={styles.value}>
                    {formatWinchMeters(value)} {unit}
                </span>
            )}
            {caption ? (
                <span
                    id={invalid && inputId ? `${inputId}-error` : undefined}
                    className={classNames(styles.caption, { [styles.captionError]: invalid })}
                    role={invalid ? 'alert' : undefined}>
                    {caption}
                </span>
            ) : null}
        </Card>
    );
}
